import {
  Typography,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  notification,
  Space,
  Tag,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import {
  useLookups,
  useCreateLookupMutation,
  useUpdateLookupMutation,
} from "admin/queries";
import { ILookupValue } from "admin/services/LookupService";
import {
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import "./LookupSetup.scss";

const CATEGORIES = [
  { key: "project_type", label: "Project Types", singular: "Project Type" },
  { key: "utility_program", label: "Utility Programs", singular: "Utility Program" },
  { key: "customer_payment_method", label: "Payment Methods", singular: "Payment Method" },
  { key: "project_status", label: "Project Statuses", singular: "Project Status" },
];

export const LookupSetup = () => {
  const [activeCategory, setActiveCategory] = useState("project_type");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ILookupValue | null>(null);
  const [form] = Form.useForm();
  const [api, notificationContextHolder] = notification.useNotification();

  // Include inactive values so admins can see and re-activate deactivated options.
  const { data, isLoading, error: fetchError } = useLookups(activeCategory, {
    includeInactive: true,
  });
  const createLookupMutation = useCreateLookupMutation();
  const updateLookupMutation = useUpdateLookupMutation();

  useEffect(() => {
    if (fetchError) {
      api.error({
        message: "Failed to load lookup values",
        description: fetchError?.message || "Please check the API server",
      });
    }
  }, [fetchError]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values: { value: string; label: string }) => {
    try {
      if (editingRecord) {
        await updateLookupMutation.mutateAsync({
          id: editingRecord.id,
          data: { label: values.label },
        });
        api.success({
          duration: 3,
          message: "Lookup Updated",
          description: "The lookup value has been renamed.",
        });
      } else {
        const items = data ?? [];
        const nextSortOrder = items.length
          ? Math.max(...items.map((i) => i.sortOrder)) + 1
          : 0;
        await createLookupMutation.mutateAsync({
          category: activeCategory,
          value: values.value,
          label: values.label,
          sortOrder: nextSortOrder,
        });
        api.success({
          duration: 3,
          message: "Lookup Created",
          description: "The lookup value has been created.",
        });
      }
      closeModal();
    } catch (error) {
      api.error({
        message: "Error",
        description: (error as Error)?.message,
      });
    }
  };

  const openEditModal = (record: ILookupValue) => {
    setEditingRecord(record);
    form.setFieldsValue({ value: record.value, label: record.label });
    setIsModalOpen(true);
  };

  const handleToggleActive = async (record: ILookupValue, isActive: boolean) => {
    try {
      await updateLookupMutation.mutateAsync({ id: record.id, data: { isActive } });
      api.success({
        duration: 3,
        message: isActive ? "Lookup Activated" : "Lookup Deactivated",
        description: isActive
          ? "The value is now available for new selections."
          : "The value stays on existing projects but is hidden from new selections.",
      });
    } catch (error) {
      api.error({ message: "Error", description: (error as Error)?.message });
    }
  };

  const handleMove = async (record: ILookupValue, direction: "up" | "down") => {
    const items = data ?? [];
    const index = items.findIndex((i) => i.id === record.id);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || swapIndex < 0 || swapIndex >= items.length) {
      return;
    }
    // Build the desired order, then re-number every row to its new position.
    // Swapping the two neighbors' sortOrder values alone is unreliable: existing
    // rows can share the same sortOrder (e.g. seeded defaults all at 0), in which
    // case a value swap is a no-op. Normalizing the whole list guarantees a move.
    const reordered = [...items];
    [reordered[index], reordered[swapIndex]] = [
      reordered[swapIndex],
      reordered[index],
    ];
    try {
      await Promise.all(
        reordered
          .map((item, position) => ({ item, position }))
          .filter(({ item, position }) => item.sortOrder !== position)
          .map(({ item, position }) =>
            updateLookupMutation.mutateAsync({
              id: item.id,
              data: { sortOrder: position },
            })
          )
      );
    } catch (error) {
      api.error({ message: "Error", description: (error as Error)?.message });
    }
  };

  const rows = data ?? [];

  const columns = [
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Type",
      dataIndex: "isDefault",
      key: "isDefault",
      render: (isDefault: boolean) =>
        isDefault ? <Tag color="blue">Default</Tag> : <Tag>Custom</Tag>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean, record: ILookupValue) => (
        <Switch
          checked={isActive}
          disabled={record.isDefault}
          loading={updateLookupMutation.isPending}
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: "Order",
      key: "order",
      render: (_: unknown, record: ILookupValue, index: number) => (
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || updateLookupMutation.isPending}
            onClick={() => handleMove(record, "up")}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === rows.length - 1 || updateLookupMutation.isPending}
            onClick={() => handleMove(record, "down")}
          />
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: ILookupValue) =>
        record.isDefault ? (
          <Typography.Text type="secondary">Protected</Typography.Text>
        ) : (
          <Button size="small" onClick={() => openEditModal(record)}>
            Rename
          </Button>
        ),
    },
  ];

  const getCategoryLabel = () => {
    return CATEGORIES.find((c) => c.key === activeCategory)?.label || "Values";
  };

  const getCategorySingularLabel = () => {
    return CATEGORIES.find((c) => c.key === activeCategory)?.singular || "Value";
  };

  return (
    <div className="lookup-setup">
      {notificationContextHolder}
      <Typography.Title className="lookup-setup-title">
        Lookup Values
      </Typography.Title>
      <Typography.Paragraph>
        Manage dropdown options for Project Type, Utility Program, Payment Method,
        and Project Status. Default values are protected — they can't be renamed
        or deactivated. You can add custom values and rename, reorder, or
        deactivate those. Deactivated values stay on existing projects but are
        hidden from new selections.
      </Typography.Paragraph>

      <Tabs
        activeKey={activeCategory}
        onChange={(key) => {
          setActiveCategory(key);
          closeModal();
        }}
        items={CATEGORIES.map((c) => ({
          key: c.key,
          label: c.label,
        }))}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRecord(null);
            form.resetFields();
            setIsModalOpen(true);
          }}
        >
          Add {getCategorySingularLabel()}
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <Modal
        title={`${editingRecord ? "Rename" : "Add"} ${getCategorySingularLabel()}`}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        forceRender
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="label"
            label="Display Label"
            rules={[{ required: true, message: "Label is required" }]}
            tooltip="User-friendly name shown in dropdowns"
          >
            <Input placeholder="e.g., New Program" />
          </Form.Item>
          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: "Value is required" }]}
            tooltip="Internal identifier used to store the selection. Cannot be changed after creation."
          >
            <Input placeholder="e.g., new_program" disabled={!!editingRecord} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createLookupMutation.isPending || updateLookupMutation.isPending
                }
              >
                {editingRecord ? "Save" : "Add"}
              </Button>
              <Button onClick={closeModal}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
